import React, { useState, useCallback, useRef, useEffect } from 'react';
import gsap from 'gsap';
import { useSound } from '../hooks/useSound';
import { flashCorrect, flashWrong, Confetti } from '../lib/animations';
import { GameHeader, ScoreBadge, CanadaMapSVG, stripAnswerLeak } from '../lib/GameVisuals';
import { MapPin, ChevronRight, Globe, Award, Zap, Compass } from 'lucide-react';

/* ─── Province name → SVG ID mapping ─── */
const PROVINCE_MAP = {
  'british columbia': 'bc', 'colombie-britannique': 'bc', 'bc': 'bc', 'b.c.': 'bc',
  'alberta': 'ab', 'ab': 'ab',
  'saskatchewan': 'sk', 'sk': 'sk',
  'manitoba': 'mb', 'mb': 'mb',
  'ontario': 'on', 'on': 'on',
  'quebec': 'qc', 'québec': 'qc', 'qc': 'qc',
  'new brunswick': 'nb', 'nouveau-brunswick': 'nb', 'nb': 'nb',
  'nova scotia': 'ns', 'nouvelle-écosse': 'ns', 'ns': 'ns',
  'prince edward island': 'pe', 'pei': 'pe', 'pe': 'pe', 'île-du-prince-édouard': 'pe',
  'newfoundland': 'nl', 'terre-neuve': 'nl', 'nl': 'nl', 'newfoundland and labrador': 'nl',
  'yukon': 'yt', 'yt': 'yt',
  'northwest territories': 'nt', 'territoires du nord-ouest': 'nt', 'nt': 'nt', 'nwt': 'nt',
  'nunavut': 'nu', 'nu': 'nu',
};

/* ─── City → Province fallback ─── */
const CITY_PROVINCE = {
  'ottawa': 'on', 'toronto': 'on', 'sudbury': 'on', 'mattawa': 'on',
  'montréal': 'qc', 'montreal': 'qc', 'québec': 'qc', 'trois-rivières': 'qc', 'sherbrooke': 'qc',
  'moncton': 'nb', 'fredericton': 'nb',
  'halifax': 'ns',
  'charlottetown': 'pe',
  'st. john\'s': 'nl',
  'winnipeg': 'mb', 'saint-laurent': 'mb', 'saint-boniface': 'mb',
  'regina': 'sk', 'gravelbourg': 'sk',
  'edmonton': 'ab', 'calgary': 'ab',
  'vancouver': 'bc', 'victoria': 'bc',
  'whitehorse': 'yt',
  'yellowknife': 'nt',
  'iqaluit': 'nu',
};

function resolveProvince(item) {
  // Try map_location.province first
  const loc = item.map_location;
  if (loc) {
    const prov = loc.province || '';
    const key = prov.toLowerCase().trim();
    if (PROVINCE_MAP[key]) return PROVINCE_MAP[key];
  }
  // Try correct answer text
  const correctOpt = (item.options || []).find(o => o.correct);
  if (correctOpt) {
    const txt = (correctOpt.text || '').toLowerCase().replace(/^(le |la |l'|les )/, '').trim();
    if (PROVINCE_MAP[txt]) return PROVINCE_MAP[txt];
    if (CITY_PROVINCE[txt]) return CITY_PROVINCE[txt];
  }
  // Try region_focus
  if (item.region_focus) {
    const key = item.region_focus.toLowerCase().trim();
    if (PROVINCE_MAP[key]) return PROVINCE_MAP[key];
  }
  return null;
}

function resolveCoords(item) {
  const loc = item.map_location;
  if (!loc) return null;
  const lat = loc.latitude || loc.lat;
  const lng = loc.longitude || loc.lng;
  if (lat && lng) return { lat, lng };
  return null;
}

/* ─── Mode Labels ─── */
const MODE_INFO = {
  trivia: { label: 'Trivia', color: '#4C6EF5', icon: Globe },
  cultural_trivia: { label: 'Culture', color: '#7950F2', icon: Award },
  navigation: { label: 'Navigate', color: '#0CA678', icon: Compass },
  direction_navigation: { label: 'Navigate', color: '#0CA678', icon: Compass },
  direction: { label: 'Direction', color: '#0CA678', icon: Compass },
  audio_locate: { label: 'Listen', color: '#E03131', icon: MapPin },
};

/* ─── Tutorial Screen ─── */
function TutorialScreen({ onStart, items }) {
  const modes = [...new Set(items.map(i => i.mode).filter(Boolean))];
  const hasFacts = items.some(i => i.fact_popup);

  return (
    <div data-testid="carte-tutorial" style={{
      position: 'fixed', inset: 0, zIndex: 100,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg, #E7F5FF 0%, #D0EBFF 40%, #F0F4F8 100%)',
      padding: '2rem',
    }}>
      <div style={{ maxWidth: 760, textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <MapPin size={48} color="#E03131" />
          <h1 style={{ fontFamily: 'Fredoka', fontSize: '3.2rem', fontWeight: 700, color: '#1A1A2E' }}>
            CARTE DU CANADA
          </h1>
        </div>
        <p style={{ fontFamily: 'Nunito', fontSize: '1.5rem', color: '#333', marginBottom: '2rem', fontWeight: 600 }}>
          Discover Canada by answering questions!
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(76,110,245,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <Globe size={40} color="#4C6EF5" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#4C6EF5', marginBottom: '0.3rem' }}>
              Answer
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              Answer questions about Canadian geography, culture, and French communities
            </div>
          </div>

          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(224,49,49,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <MapPin size={40} color="#E03131" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#E03131', marginBottom: '0.3rem' }}>
              Discover
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              Watch locations light up on the map as you answer correctly!
            </div>
          </div>

          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(245,159,0,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <Zap size={40} color="#F59F00" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#F59F00', marginBottom: '0.3rem' }}>
              Streak!
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              {hasFacts ? 'Answer correctly to discover fun facts about Canada!' : 'Build your streak by answering consecutive questions right!'}
            </div>
          </div>
        </div>

        {modes.length > 0 && (
          <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            {modes.slice(0, 5).map(m => {
              const info = MODE_INFO[m] || { label: m, color: '#4C6EF5' };
              return (
                <span key={m} style={{
                  padding: '0.2rem 0.7rem', borderRadius: '2rem',
                  background: `${info.color}12`, fontFamily: 'Fredoka',
                  fontSize: '0.9rem', fontWeight: 700, color: info.color,
                }}>
                  {info.label}
                </span>
              );
            })}
          </div>
        )}

        <button data-testid="carte-tutorial-start" onClick={onStart} className="btn-arcade btn-primary"
          style={{ fontSize: '1.8rem', padding: '0 3rem', minHeight: 72, gap: '0.75rem', borderBottomWidth: 8 }}>
          <ChevronRight size={32} /> Explore Canada!
        </button>
      </div>
    </div>
  );
}

export function CarteDuCanada({ items: rawItems, settings, onGameEnd }) {
  const { playSound, playGameMusic, stopMusic } = useSound();
  useEffect(() => { playGameMusic('carte_du_canada'); return () => stopMusic(); }, [playGameMusic, stopMusic]);
  const items = rawItems.filter(i => i.options && i.options.length > 0);
  const maxRounds = Math.min(settings.rounds, items.length);

  const [showTutorial, setShowTutorial] = useState(true);
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [totalAnswered, setTotalAnswered] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [correctIdx, setCorrectIdx] = useState(null);
  const [showConfetti, setShowConfetti] = useState(0);
  const [litLocations, setLitLocations] = useState([]);
  const [litProvinces, setLitProvinces] = useState([]);
  const [showFact, setShowFact] = useState(null); // fact_popup or cultural_fact_fr
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const scoreRef = useRef(null);
  const streakRef = useRef(null);
  const mapRef = useRef(null);
  const endedRef = useRef(false);

  // Value refs
  const scoreValRef = useRef(0);
  const bestStreakValRef = useRef(0);
  const totalAnsweredRef = useRef(0);
  const reviewRef = useRef([]);
  scoreValRef.current = score;
  bestStreakValRef.current = bestStreak;
  totalAnsweredRef.current = totalAnswered;

  const item = items[idx];

  const handleAnswer = useCallback((optIdx, btnEl) => {
    if (feedback || !item) return;
    const correct = item.options?.[optIdx]?.correct;
    setTotalAnswered(a => a + 1);

    if (correct) {
      playSound('correct');
      setScore(s => s + 1);
      setFeedback('correct');
      if (btnEl) flashCorrect(btnEl);
      if (scoreRef.current) gsap.fromTo(scoreRef.current, { scale: 1.4 }, { scale: 1, duration: 0.4, ease: 'elastic.out(1,0.5)' });

      // Light up province
      const prov = resolveProvince(item);
      if (prov && !litProvinces.includes(prov)) {
        setLitProvinces(prev => [...prev, prov]);
      }

      // Add map pin
      const coords = resolveCoords(item);
      if (coords) {
        setLitLocations(prev => [...prev, coords]);
      }

      // Pulse map
      if (mapRef.current) gsap.fromTo(mapRef.current, { scale: 1.02 }, { scale: 1, duration: 0.4, ease: 'elastic.out(1,0.5)' });

      // Track correct for review
      const correctOpt = item.options.find(o => o.correct);
      reviewRef.current.push({
        prompt: item.prompt_text_fr || item.prompt_text_en || '?',
        correctAnswer: correctOpt?.text || '—',
        userAnswer: item.options[optIdx]?.text || '—', wasCorrect: true,
        type: (MODE_INFO[item.mode] || {}).label,
      });

      // Streak
      setStreak(s => {
        const newStreak = s + 1;
        setBestStreak(b => Math.max(b, newStreak));
        return newStreak;
      });
      if (streakRef.current) gsap.fromTo(streakRef.current, { scale: 1.5 }, { scale: 1, duration: 0.4, ease: 'elastic.out(1,0.5)' });

      // Show fact popup
      const fact = item.fact_popup || (item.cultural_fact_fr ? { desc_fr: item.cultural_fact_fr } : null);
      if (fact) {
        setShowFact(fact);
        setTimeout(() => { setShowFact(null); advance(); }, 3000);
      } else {
        setTimeout(() => advance(), 1000);
      }
    } else {
      playSound('wrong');
      setFeedback('wrong');
      if (btnEl) flashWrong(btnEl);
      setStreak(0);
      const ci = item.options.findIndex(o => o.correct);
      setCorrectIdx(ci);
      // Track wrong for review
      const correctOpt = item.options.find(o => o.correct);
      reviewRef.current.push({
        prompt: item.prompt_text_fr || item.prompt_text_en || '?',
        correctAnswer: correctOpt?.text || '—',
        userAnswer: item.options[optIdx]?.text || '—', wasCorrect: false,
        type: (MODE_INFO[item.mode] || {}).label,
      });
      setTimeout(() => advance(), 1500);
    }
  }, [feedback, item, idx, maxRounds, playSound, litProvinces]);

  const advance = useCallback(() => {
    setFeedback(null);
    setCorrectIdx(null);
    setShowFact(null);
    const next = idx + 1;
    if (next >= maxRounds) endGame();
    else setIdx(next);
  }, [idx, maxRounds]);

  const endGame = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    setShowConfetti(c => c + 1);
    playSound('win');
    const finalScore = scoreValRef.current;
    const finalTotal = totalAnsweredRef.current;
    const finalBestStreak = bestStreakValRef.current;
    setTimeout(() => onGameEnd({
      team1Score: finalScore,
      team2Score: 0,
      totalQuestions: finalTotal,
      accuracy: finalTotal > 0 ? (finalScore / finalTotal) * 100 : 0,
      bestStreak: finalBestStreak,
      questionReview: (() => {
        const review = [...reviewRef.current];
        for (let i = review.length; i < maxRounds && i < items.length; i++) {
          const it = items[i]; if (!it) break;
          const correctOpt = (it.options || []).find(o => o.correct);
          review.push({ prompt: it.prompt_text_fr || it.prompt_text_en || '?', correctAnswer: correctOpt?.text || '—', userAnswer: null, wasCorrect: null, type: (MODE_INFO[it.mode] || {}).label });
        }
        return review;
      })(),
      reviewType: 'qa',
    }), 1500);
  }, [onGameEnd, playSound]);

  // Skip-to-end: parent fires this when teacher hits "Skip" in the game menu
  useEffect(() => {
    const onForceEnd = () => endGame();
    window.addEventListener('frenchboard:end-game-now', onForceEnd);
    return () => window.removeEventListener('frenchboard:end-game-now', onForceEnd);
  }, [endGame]);

  if (!item && !showTutorial) {
    return (
      <div style={{
        height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg)', color: 'var(--text-muted)', fontSize: '1.5rem', fontFamily: 'Fredoka',
      }}>
        No map content available
      </div>
    );
  }

  const progress = maxRounds > 0 ? (idx / maxRounds) * 100 : 0;
  const modeInfo = MODE_INFO[item?.mode] || { label: 'Question', color: '#4C6EF5', icon: Globe };
  const ModeIcon = modeInfo.icon || Globe;

  return (
    <div data-testid="carte-du-canada-game" style={{
      height: '100vh', display: 'flex', flexDirection: 'column',
      background: 'linear-gradient(180deg, #E7F5FF 0%, #F0F4F8 100%)',
      position: 'relative', overflow: 'hidden',
    }}>
      <Confetti trigger={showConfetti} />

      {showTutorial && <TutorialScreen onStart={() => setShowTutorial(false)} items={items.slice(0, maxRounds)} />}

      <GameHeader title="CARTE DU CANADA" icon={<MapPin size={24} color="#E03131" />} color="#E03131">
        <ScoreBadge label="DISCOVERED" value={`${score}/${maxRounds}`} color="var(--primary)" scoreRef={scoreRef} />
        {streak >= 2 && (
          <div ref={streakRef} data-testid="carte-streak" style={{
            display: 'flex', alignItems: 'center', gap: '0.3rem',
            background: 'rgba(245,159,0,0.1)', padding: '0.2rem 0.75rem', borderRadius: '2rem',
            border: '2px solid rgba(245,159,0,0.3)',
          }}>
            <Zap size={18} color="#F59F00" fill="#F59F00" />
            <span style={{ fontFamily: 'Fredoka', fontSize: '1.2rem', fontWeight: 800, color: '#F59F00' }}>{streak}</span>
          </div>
        )}
        <div style={{ fontFamily: 'Fredoka', fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600 }}>
          Round {idx + 1}/{maxRounds}
        </div>
      </GameHeader>

      {/* Progress bar */}
      <div style={{ height: 5, background: 'rgba(0,0,0,0.05)' }}>
        <div style={{
          height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg, #E03131, #FF6B6B)',
          transition: 'width 0.5s ease', borderRadius: '0 3px 3px 0',
        }} />
      </div>

      {item && (
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1.6fr 1fr', overflow: 'hidden' }}>
          {/* Map panel */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0.75rem', position: 'relative' }}>
            <div ref={mapRef} style={{
              width: '100%', height: '100%', maxHeight: 'calc(100vh - 80px)', borderRadius: '1.25rem',
              overflow: 'hidden', border: '2px solid rgba(0,0,0,0.08)',
              position: 'relative',
              background: 'linear-gradient(180deg, #87CEEB 0%, #B0D4E8 40%, #8FC5E0 100%)',
              boxShadow: '0 6px 24px rgba(0,0,0,0.1)',
            }}>
              <div style={{ position: 'absolute', inset: '3%' }}>
                <CanadaMapSVG litProvinces={litProvinces} />
              </div>

              {/* Pins for correct locations */}
              {litLocations.map((loc, i) => {
                const left = 30 + ((loc.lng + 140) / 80) * 50;
                const top = 15 + ((70 - loc.lat) / 30) * 55;
                return (
                  <div key={i} style={{
                    position: 'absolute',
                    left: `${Math.max(5, Math.min(95, left))}%`,
                    top: `${Math.max(5, Math.min(90, top))}%`,
                    transform: 'translate(-50%, -100%)',
                    zIndex: 10,
                  }}>
                    <MapPin size={24} style={{ color: '#E03131', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} fill="#E03131" />
                  </div>
                );
              })}

              {/* Provinces discovered counter */}
              <div style={{
                position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)',
                background: 'rgba(255,255,255,0.9)', padding: '0.2rem 0.75rem', borderRadius: '1rem',
                fontFamily: 'Fredoka', fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-primary)',
                backdropFilter: 'blur(4px)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}>
                {litProvinces.length} / 13 provinces
              </div>
            </div>
          </div>

          {/* Question panel */}
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '1.25rem 1.5rem', borderLeft: '2px solid rgba(0,0,0,0.06)', overflow: 'auto' }}>
            {/* Mode badge */}
            <div style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <ModeIcon size={22} color={modeInfo.color} />
              <span style={{
                fontFamily: 'Fredoka', fontSize: '1rem', fontWeight: 700, color: modeInfo.color,
                textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>
                {modeInfo.label}
              </span>
            </div>

            {/* Question */}
            <div data-testid="question-card" style={{
              background: 'rgba(255,255,255,0.95)', borderRadius: '1.25rem', padding: '1.25rem 1.5rem',
              border: '2px solid rgba(0,0,0,0.06)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
              marginBottom: '1rem',
            }}>
              {item.prompt_text_en && (
                <div style={{ fontSize: '1.15rem', color: 'var(--text-secondary)', fontWeight: 600, lineHeight: 1.3, marginBottom: item.prompt_text_fr ? '0.4rem' : 0 }}>
                  {stripAnswerLeak(item.prompt_text_en, (item.options || []).find(o => o.correct)?.text)}
                </div>
              )}
              {item.prompt_text_fr && (
                <div style={{ fontFamily: 'Fredoka', fontSize: 'clamp(1.4rem, 2.5vw, 1.9rem)', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>
                  {stripAnswerLeak(item.prompt_text_fr, (item.options || []).find(o => o.correct)?.text)}
                </div>
              )}
            </div>

            {/* Options */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {(item.options || []).map((opt, i) => {
                const isCorrectHighlight = feedback === 'wrong' && correctIdx === i;
                return (
                  <button key={i} data-testid={`carte-opt-${i}`}
                    onClick={(e) => handleAnswer(i, e.currentTarget)} disabled={!!feedback}
                    style={{
                      padding: '1rem 1.5rem', borderRadius: '1rem',
                      background: isCorrectHighlight ? 'rgba(47,158,68,0.1)' : 'rgba(255,255,255,0.95)',
                      border: isCorrectHighlight ? '3px solid var(--success)' : '2px solid rgba(0,0,0,0.06)',
                      borderBottom: isCorrectHighlight ? '3px solid #2B8A3E' : '5px solid rgba(0,0,0,0.06)',
                      cursor: feedback ? 'default' : 'pointer',
                      touchAction: 'manipulation',
                      boxShadow: isCorrectHighlight ? '0 0 8px rgba(47,158,68,0.15)' : '0 2px 8px rgba(0,0,0,0.04)',
                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                      transition: 'transform 0.1s, border-color 0.3s, background 0.3s',
                      textAlign: 'left',
                    }}
                    onMouseDown={e => { if (!feedback) e.currentTarget.style.transform = 'translateY(2px)'; }}
                    onMouseUp={e => e.currentTarget.style.transform = ''}
                  >
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 40, height: 40, borderRadius: '50%',
                      background: isCorrectHighlight ? 'rgba(47,158,68,0.15)' : 'rgba(224,49,49,0.08)',
                      color: isCorrectHighlight ? 'var(--success)' : '#E03131',
                      fontSize: '1.1rem', fontWeight: 700, fontFamily: 'Fredoka',
                      flexShrink: 0,
                    }}>{opt.label || String.fromCharCode(65 + i)}</span>
                    <span style={{
                      flex: 1, fontSize: 'clamp(1.1rem, 2vw, 1.35rem)', fontWeight: 600,
                      color: isCorrectHighlight ? 'var(--success)' : 'var(--text-primary)',
                    }}>
                      {opt.text}
                    </span>
                    {isCorrectHighlight && (
                      <span style={{ fontFamily: 'Fredoka', fontSize: '0.9rem', fontWeight: 700, color: 'var(--success)' }}>
                        Correct
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Fact popup */}
            {showFact && (
              <div data-testid="fact-popup" style={{
                marginTop: '0.75rem', padding: '0.75rem 1rem', borderRadius: '1rem',
                background: 'linear-gradient(135deg, #FFF9DB 0%, #FFF3BF 100%)',
                border: '2px solid var(--accent)',
                boxShadow: '0 4px 16px rgba(245,159,0,0.15)',
                animation: 'popIn 0.3s ease-out',
              }}>
                <style>{`@keyframes popIn { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
                {showFact.title_fr && (
                  <div style={{ fontFamily: 'Fredoka', fontSize: '1rem', fontWeight: 700, color: '#E67700', marginBottom: '0.25rem' }}>
                    {showFact.title_fr}
                  </div>
                )}
                <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.4 }}>
                  {showFact.desc_fr || showFact.desc_en || ''}
                </div>
                {showFact.desc_en && showFact.desc_fr && (
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.3rem', fontStyle: 'italic' }}>
                    {showFact.desc_en}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
